/**
 * Branch Management API Client
 * Handles all API calls for branch CRUD operations
 */

import axios, { getBusinessId } from '@/lib/api/axios-instance';

const BASE_URL = '/api/v2/business';

// ============================================================================
// Types
// ============================================================================

export interface Branch {
  id: string;
  business_id: string;
  branch_name: string;
  branch_code: string | null;
  branch_type: 'physical' | 'virtual' | 'mobile' | 'online';
  is_active: boolean;
  is_main_branch: boolean;
  account_count?: number;
  created_at: string;
  updated_at: string;
  accounts?: BranchAccountSummary[];
}

export interface BranchAccountSummary {
  id: string;
  branch_id: string;
  account_source: 'bank' | 'promptpay' | 'shop' | 'truemoney';
  account_name: string;
  account_number?: string;
  bank_code?: string;
  is_active: boolean;
}

export interface CreateBranchRequest {
  branch_name: string;
  branch_code?: string;
  branch_type?: 'physical' | 'virtual' | 'mobile' | 'online';
  account_ids?: string[];
}

export interface UpdateBranchRequest {
  branch_name?: string;
  branch_code?: string;
  branch_type?: 'physical' | 'virtual' | 'mobile' | 'online';
  account_ids?: string[];
  is_active?: boolean;
}

export interface BranchListResponse {
  success: boolean;
  data: Branch[];
}

export interface BranchResponse {
  success: boolean;
  data: Branch;
}

// ============================================================================
// API Functions
// ============================================================================

export const branchesAPI = {
  /**
   * Get all branches for current business
   */
  async getAll(): Promise<Branch[]> {
    const response = await axios.get<BranchListResponse>(`${BASE_URL}/branches`);
    return response.data.data;
  },

  /**
   * Get single branch by ID
   */
  async getById(branchId: string): Promise<Branch> {
    const response = await axios.get<BranchResponse>(`${BASE_URL}/branches/${branchId}`);
    return response.data.data;
  },

  /**
   * Create new branch
   */
  async create(data: CreateBranchRequest): Promise<Branch> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please login again.');
    }
    const response = await axios.post<BranchResponse>(
      `${BASE_URL}/businesses/${businessId}/branches`, 
      data
    );
    return response.data.data;
  },

  /**
   * Update existing branch
   */
  async update(branchId: string, data: UpdateBranchRequest): Promise<Branch> {
    const response = await axios.put<BranchResponse>(
      `${BASE_URL}/branches/${branchId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete branch
   */
  async delete(branchId: string): Promise<void> {
    await axios.delete(`${BASE_URL}/branches/${branchId}`);
  },
};

export default branchesAPI;
